/**
 * Teacher classes: real API (GET /api/classes/by-teacher/{teacherId}).
 */

import { createAuthenticatedApi } from "@/lib/utils";
import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { getEffectiveTenantApiHeaders } from "@/lib/utils/env/effective-tenant";
import { getStudioClock, studioTodayDate } from "@/lib/utils/date/studio-clock";
import { ClassesApi } from "@/openapi/generated";
import { BASE_PATH } from "@/openapi/generated/runtime";
import type { GetClassResponse } from "@/openapi/generated/models";

const apiBaseUrl =
  (typeof process !== "undefined" &&
    process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, "")) ||
  BASE_PATH;

export interface TeacherClassItem {
  classId: number;
  name: string;
  day: string;
  startTime: string;
  endTime: string;
  location?: string;
  classroom?: string;
  nextOccurrence?: string;
  enrolledCount?: number;
  maxStudents?: number;
}

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

function getNextOccurrenceForDay(
  dayName: string,
  startTime: string | null,
): Date {
  const now = new Date();
  const clock = getStudioClock(now);
  const today = studioTodayDate(clock);
  const targetDay = dayNames.indexOf(dayName);
  if (targetDay === -1) return today;
  let daysUntil = targetDay - clock.weekdayIndex;
  if (daysUntil < 0) daysUntil += 7;
  if (daysUntil === 0 && startTime) {
    const [h, m] = startTime.split(":").map(Number);
    const startMinutes = (h ?? 0) * 60 + (m ?? 0);
    if (clock.minutesFromMidnight > startMinutes) daysUntil = 7;
  }
  const next = new Date(today);
  next.setDate(today.getDate() + daysUntil);
  if (startTime) {
    const [h, m] = startTime.split(":").map(Number);
    next.setHours(h ?? 0, m ?? 0, 0, 0);
  }
  return next;
}

function mapClassToTeacherItem(c: GetClassResponse): TeacherClassItem {
  const firstDay = c.classDays?.[0];
  const day = firstDay?.day ?? "Monday";
  const startTime =
    firstDay?.startTime ?? c.classStartTime ?? "09:00";
  const endTime = firstDay?.endTime ?? c.classEndTime ?? "10:00";

  let nextOccurrence: Date | null = null;
  for (const cd of c.classDays ?? []) {
    const d = getNextOccurrenceForDay(cd.day, cd.startTime ?? null);
    if (!nextOccurrence || d < nextOccurrence) nextOccurrence = d;
  }
  if (!nextOccurrence) {
    nextOccurrence = getNextOccurrenceForDay(day, startTime);
  }

  return {
    classId: Number(c.classId),
    name: c.name,
    day,
    startTime,
    endTime,
    location: c.location?.name ?? undefined,
    classroom: c.classRoom?.name ?? undefined,
    nextOccurrence: nextOccurrence?.toISOString?.() ?? undefined,
    enrolledCount: Number(c.currentNumberOfStudents) ?? 0,
    maxStudents: c.maxNumberOfStudents != null ? Number(c.maxNumberOfStudents) : undefined,
  };
}

function unwrapClassesArray(json: unknown): unknown[] {
  if (Array.isArray(json)) return json;
  if (json && typeof json === "object") {
    const o = json as Record<string, unknown>;
    const nested = o.data ?? o.items ?? o.result ?? o.classes;
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

/** Map a loosely-typed API object when OpenAPI deserialization fails (e.g. null classDays). */
function mapRawClassToTeacherItem(c: Record<string, unknown>): TeacherClassItem {
  const classDays = (Array.isArray(c.classDays)
    ? c.classDays
    : Array.isArray(c.class_days)
      ? c.class_days
      : []) as Array<{
        day?: string;
        startTime?: string | null;
        endTime?: string | null;
        start_time?: string | null;
        end_time?: string | null;
    }>;

  const firstDay = classDays[0];
  const day = (firstDay?.day as string) ?? "Monday";
  const startTime =
    (firstDay?.startTime ??
      firstDay?.start_time ??
      c.classStartTime ??
      c.class_start_time ??
      "09:00") as string;
  const endTime =
    (firstDay?.endTime ??
      firstDay?.end_time ??
      c.classEndTime ??
      c.class_end_time ??
      "10:00") as string;

  const classId = Number(c.classId ?? c.class_id ?? 0);
  const name = String(c.name ?? "Class");

  let nextOccurrence: Date | null = null;
  for (const cd of classDays) {
    const dName = (cd.day as string) ?? day;
    const st = (cd.startTime ?? cd.start_time ?? startTime) as string;
    const d = getNextOccurrenceForDay(dName, st);
    if (!nextOccurrence || d < nextOccurrence) nextOccurrence = d;
  }
  if (!nextOccurrence) {
    nextOccurrence = getNextOccurrenceForDay(day, startTime);
  }

  const loc = (c.location ?? c.Location) as Record<string, unknown> | undefined;
  const room = (c.classRoom ?? c.class_room) as Record<string, unknown> | undefined;

  return {
    classId,
    name,
    day,
    startTime,
    endTime,
    location: loc?.name != null ? String(loc.name) : undefined,
    classroom: room?.name != null ? String(room.name) : undefined,
    nextOccurrence: nextOccurrence?.toISOString?.() ?? undefined,
    enrolledCount: Number(c.currentNumberOfStudents ?? c.current_number_of_students) || 0,
    maxStudents:
      c.maxNumberOfStudents != null || c.max_number_of_students != null
        ? Number(c.maxNumberOfStudents ?? c.max_number_of_students)
        : undefined,
  };
}

async function getTeacherClassesLenient(
  accessToken: string,
  teacherId: number,
): Promise<TeacherClassItem[]> {
  const res = await fetch(
    `${apiBaseUrl}/api/classes/by-teacher/${encodeURIComponent(String(teacherId))}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        ...getEffectiveTenantApiHeaders(),
        ...getActiveRoleRequestHeaders(),
      },
    },
  );
  if (!res.ok) {
    throw new Error(`Teacher classes request failed: ${res.status}`);
  }
  const json: unknown = await res.json();
  return unwrapClassesArray(json).map((row) =>
    mapRawClassToTeacherItem(
      row && typeof row === "object" ? (row as Record<string, unknown>) : {},
    ),
  );
}

export async function getTeacherClasses(
  accessToken: string,
  teacherId: number,
): Promise<TeacherClassItem[]> {
  const api = createAuthenticatedApi(ClassesApi, accessToken);
  try {
    const classes = await api.apiClassesByTeacherTeacherIdGet({ teacherId });
    return (classes ?? []).map(mapClassToTeacherItem);
  } catch {
    return getTeacherClassesLenient(accessToken, teacherId);
  }
}
